import { THEME_PREFERENCES_STORAGE_KEY, THEME_PREFERENCES_VERSION } from './constants';
import { DEFAULT_THEME_PREFERENCES, type ThemeMode, type ThemePreferences } from './types';

const isThemeMode = (value: unknown): value is ThemeMode => {
  return value === 'light' || value === 'dark' || value === 'auto';
};

const normalizeThemePreferences = (value: ThemePreferences): ThemePreferences => ({
  themeMode: isThemeMode(value.themeMode) ? value.themeMode : DEFAULT_THEME_PREFERENCES.themeMode,
  version: typeof value.version === 'number' ? value.version : THEME_PREFERENCES_VERSION,
  updatedAt: value.updatedAt,
});

const isThemePreferences = (value: unknown): value is ThemePreferences => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Partial<ThemePreferences>;
  return isThemeMode(candidate.themeMode);
};

export const readThemePreferencesFromStorage = (): ThemePreferences => {
  try {
    const raw = localStorage.getItem(THEME_PREFERENCES_STORAGE_KEY);
    if (!raw) {
      return DEFAULT_THEME_PREFERENCES;
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!isThemePreferences(parsed)) {
      return DEFAULT_THEME_PREFERENCES;
    }
    return normalizeThemePreferences(parsed);
  } catch {
    return DEFAULT_THEME_PREFERENCES;
  }
};

export const writeThemePreferencesToStorage = (value: ThemePreferences): ThemePreferences => {
  const normalized: ThemePreferences = {
    ...normalizeThemePreferences(value),
    updatedAt: value.updatedAt ?? new Date().toISOString(),
  };
  localStorage.setItem(THEME_PREFERENCES_STORAGE_KEY, JSON.stringify(normalized));
  return normalized;
};
